import { getMarkets } from "@dotcom/markets";
import { getAlternateLinks, getPageMeta, mergeMeta } from "@dotcom/remix-meta";
import {
  Breadcrumbs,
  Section,
  Spacer,
  Spacings,
} from "@polestar/component-warehouse-react";
import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData, useParams } from "@remix-run/react";
import { type StructuredTextGraphQlResponse } from "datocms-structured-text-to-plain-text";
import { useRef } from "react";

import {
  ChangelogDrawer,
  ContentWithHtml,
  ContentWithStructuredText,
} from "./components";
import { ChangelogOpenProvider } from "./contexts/ChangelogOpen";
import getDocumentPageData from "./data/getDocumentPageDataBySlug";

import { getErrorPageData } from "~/common/data";
import { getIsChina } from "~/common/helpers/.server";
import { type Maybe } from "~/common/types";
import { type RootLoader } from "~/root";
import { type DocumentStructuredText } from "~/types";

export const meta = mergeMeta<typeof loader, { root: RootLoader }>(
  ({ data, location }) => {
    if (!data) return [];

    const { seoMetaTag, hasNoRobotMeta } = data.documentPageData;

    return [
      ...getPageMeta({
        title: seoMetaTag?.title,
        description: seoMetaTag?.description,
        image: seoMetaTag?.image?.url,
        twitterCard: seoMetaTag?.twitterCard,
        noIndex: hasNoRobotMeta,
      }),
      ...getAlternateLinks(
        location.pathname,
        data.publishedMarkets,
        data.isChina
      ),
    ];
  }
);

export const loader = async ({
  context: { market, datoClient },
  params,
  request,
}: LoaderFunctionArgs) => {
  if (!market || !params.documentSlug || !params.categorySlug) {
    throw json(
      await getErrorPageData(datoClient, { market, requestUrl: request.url }),
      { status: 404 }
    );
  }

  const data = await getDocumentPageData(datoClient, {
    slug: String(params.documentSlug),
    categorySlug: String(params.categorySlug),
    market,
  });

  if (!data?.documentPageData) {
    throw json(
      await getErrorPageData(datoClient, { market, requestUrl: request.url }),
      { status: 404 }
    );
  }

  const isChina = getIsChina();
  return {
    publishedMarkets: data.publishedMarkets,
    documentPageData: data.documentPageData,
    landingPageData: data.landingPageData,
    dictionary: data.dictionary,
    market,
    isChina,
    markets: getMarkets(datoClient.isPreview, isChina),
  };
};

const handleDownloadPdf = () => {
  window.print();
};

const hasSimpleContent = (
  content: Maybe<DocumentStructuredText[]>,
  contentSimple: Maybe<string>
): contentSimple is string => {
  return Boolean(!content?.length && contentSimple);
};

const DocumentPage = () => {
  const {
    dictionary,
    market,
    documentPageData: {
      content,
      contentSimple,
      inPageNavigationLinks,
      title: documentPageTitle,
      majorVersion,
      minorVersion,
      changelogDescription,
      changelog,
      versionDate,
      parentPage,
      initiallyExpandedAccordionPanels,
    },
    landingPageData: { title: landingPageTitle },
  } = useLoaderData<typeof loader>();

  const sectionRefs = useRef<HTMLElement[]>([]);

  const { locale } = useParams();

  const version =
    majorVersion !== null && minorVersion !== null
      ? `v${String(majorVersion)}.${String(minorVersion)}`
      : undefined;

  if (!content?.length && !contentSimple) {
    return null;
  }

  return (
    <ChangelogOpenProvider>
      {changelogDescription && (
        <ChangelogDrawer
          subtitle={documentPageTitle || ""}
          title={dictionary["What is new?"]}
          description={changelogDescription}
          changelog={changelog as unknown as StructuredTextGraphQlResponse}
          market={market}
        />
      )}

      <Section>
        {locale && (
          <Breadcrumbs
            links={[
              { href: `/${locale}/legal/`, label: landingPageTitle },
              {
                href: `/${locale}/legal/${parentPage?.slug}/`,
                label: parentPage?.title,
              },
              { href: "#", label: documentPageTitle },
            ]}
          />
        )}

        <Spacer
          spacing={{
            mobile: Spacings.medium,
            tablet: Spacings.large,
          }}
        />

        {hasSimpleContent(content, contentSimple) ? (
          <ContentWithHtml
            changelogDescription={changelogDescription}
            contentSimple={contentSimple}
            dictionary={dictionary}
            handleDownloadPdf={handleDownloadPdf}
            localizedDate={versionDate}
            title={documentPageTitle}
            version={version}
          />
        ) : (
          <ContentWithStructuredText
            changelogDescription={changelogDescription}
            content={content}
            dictionary={dictionary}
            handleDownloadPdf={handleDownloadPdf}
            initiallyExpandedAccordionPanels={initiallyExpandedAccordionPanels}
            inPageNavigationLinks={inPageNavigationLinks}
            localizedDate={versionDate}
            sectionRefs={sectionRefs}
            title={documentPageTitle}
            version={version}
          />
        )}
      </Section>
    </ChangelogOpenProvider>
  );
};

export default DocumentPage;
